import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { besprechungFachteil, besprechungLeererKopf, besprechungMinuten, type BesprechungFortschreibungAntwort, type BesprechungInhalt, type BesprechungKonfig, type SiteDocDetail } from '@symphenia/shared-types'
import { datumOhneVorlage, Feldgruppe, FeldZeile, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import type { ErpDetailProps } from '../registry'
import { besprechungFortschreibung, besprechungKonfig, besprechungKopf } from './api'
import { BesprechungDiktat } from './BesprechungDiktat'
import { BesprechungErpVorzeige } from './BesprechungErpVorzeige'
import { EinwaendeBlock } from './EinwaendeBlock'
import { PunkteBlock, TagesordnungBlock } from './PunkteBlock'
import { TeilnehmerBlock, useTeilnehmerVorschlaege } from './TeilnehmerBlock'
import { VerteilerDialog } from './VerteilerDialog'

/** Der Inhalt aus dem Detail — Kopf samt Teilnehmern und Punkten (der Server führt sie in eigenen Tabellen). */
export const inhaltVon = (d: SiteDocDetail): BesprechungInhalt => {
  const c = (d.content ?? {}) as Partial<BesprechungInhalt>
  return { ...besprechungLeererKopf(), ...c, tagesordnung: c.tagesordnung ?? [], berichtigungen: c.berichtigungen ?? [], teilnehmer: c.teilnehmer ?? [], punkte: c.punkte ?? [] } as BesprechungInhalt
}

const ZEIT = (w: unknown) => (!w || /^\d{1,2}:\d{2}$/.test(String(w)) ? null : 'HH:MM')

/** Kopf des Protokolls — eine Speicherstelle; gesendet wird nur, was sich ändert, der Server führt zusammen. */
function KopfBesprechung({ dok, i, arten, vergeben, onGeaendert }: { dok: SiteDocDetail; i: BesprechungInhalt; arten: string[]; vergeben: number[]; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const daten = useMemo<Datensatz>(
    () => ({
      art: i.art,
      laufnummer: i.laufnummer,
      beginn: i.beginn,
      ende: i.ende,
      ort: i.ort,
      leitung: i.leitung,
      naechsteDatum: i.naechste?.datum ?? null,
      naechsteBeginn: i.naechste?.beginn ?? null,
      naechsteOrt: i.naechste?.ort ?? null,
      einspruchTage: i.einspruch?.tage ?? null,
      einspruchText: i.einspruch?.text ?? null,
      bemerkung: i.bemerkung,
    }),
    [i],
  )
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungKopf:${dok.id}`,
    bezeichnung: t('siteDocs.meeting.head'),
    daten,
    schreibe: async (patch) => {
      const n = { ...daten, ...patch }
      const inhalt: Record<string, unknown> = {}
      for (const k of ['art', 'laufnummer', 'beginn', 'ende', 'ort', 'leitung', 'bemerkung'] as const) if (k in patch) inhalt[k] = n[k]
      if ('naechsteDatum' in patch || 'naechsteBeginn' in patch || 'naechsteOrt' in patch) inhalt.naechste = n.naechsteDatum ? { datum: n.naechsteDatum, beginn: n.naechsteBeginn ?? null, ort: n.naechsteOrt ?? null } : null
      if ('einspruchTage' in patch || 'einspruchText' in patch) inhalt.einspruch = n.einspruchText ? { tage: Number(n.einspruchTage) || 0, text: n.einspruchText } : null
      onGeaendert(await besprechungKopf(dok.id, inhalt))
      return n
    },
  }) as Speicherstelle<unknown>
  const protokoll = i.form === 'PROTOKOLL'
  const art = useFeld(stelle, 'art', { art: 'auswahl', label: t('siteDocs.meeting.type') })
  const laufnummer = useFeld<number | null>(stelle, 'laufnummer', { art: 'zahl', pflicht: true, stellen: 0, label: t('siteDocs.meeting.sequence'), pruefe: (w) => (w !== null && (!Number.isInteger(w) || w < 1) ? t('siteDocs.meeting.sequenceInvalid') : null) })
  const ort = useFeld(stelle, 'ort', { label: t('siteDocs.meeting.place') })
  const leitung = useFeld(stelle, 'leitung', { label: t('siteDocs.meeting.chair') })
  const bemerkung = useFeld(stelle, 'bemerkung', { label: t('siteDocs.meeting.remark') })
  const doppelt = protokoll && vergeben.includes(i.laufnummer)
  return (
    <div className="space-y-1">
      {protokoll && (
        <>
          <FeldZeile label={t('siteDocs.meeting.type')} status={art}>
            <select className={feldKlasse} {...art.props}>
              {[...new Set([...arten, i.art].filter(Boolean))].map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </FeldZeile>
          <FeldZeile label={t('siteDocs.meeting.sequence')} status={laufnummer} hilfe={doppelt ? <span className="text-amber-800 dark:text-amber-200">{t('siteDocs.meeting.sequenceTaken')}</span> : undefined}>
            <input className={`${feldKlasse} w-24`} inputMode="numeric" {...laufnummer.props} />
          </FeldZeile>
          <Feldgruppe stelle={stelle} label={t('siteDocs.meeting.time')} pruefe={(w) => {
              const a = besprechungMinuten(w.beginn as string | null)
              const b = besprechungMinuten(w.ende as string | null)
              return a !== null && b !== null && b < a ? t('siteDocs.meeting.timeInvalid') : null
            }}>
            <ZeitFelder stelle={stelle} />
          </Feldgruppe>
          <FeldZeile label={t('siteDocs.meeting.place')} status={ort}>
            <input className={feldKlasse} {...ort.props} />
          </FeldZeile>
          <FeldZeile label={t('siteDocs.meeting.chair')} status={leitung}>
            <input className={feldKlasse} {...leitung.props} />
          </FeldZeile>
          <Feldgruppe stelle={stelle} label={t('siteDocs.meeting.next')}>
            <NaechsteFelder stelle={stelle} />
          </Feldgruppe>
          <Feldgruppe stelle={stelle} label={t('siteDocs.meeting.objectionPeriod')}>
            <EinspruchFelder stelle={stelle} />
          </Feldgruppe>
        </>
      )}
      <FeldZeile label={t('siteDocs.meeting.remark')} status={bemerkung}>
        <textarea className={`${feldKlasse} min-h-16`} {...bemerkung.props} />
      </FeldZeile>
    </div>
  )
}

function ZeitFelder({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const beginn = useFeld(stelle, 'beginn', { label: t('siteDocs.meeting.from'), pruefe: ZEIT })
  const ende = useFeld(stelle, 'ende', { label: t('siteDocs.meeting.to'), pruefe: ZEIT })
  return (
    <FeldZeile label={t('siteDocs.meeting.time')} status={beginn.fehler ? beginn : ende}>
      <span className="flex items-center gap-1">
        <input className={`${feldKlasse} w-24`} placeholder="09:00" aria-label={t('siteDocs.meeting.from')} {...beginn.props} />–
        <input className={`${feldKlasse} w-24`} placeholder="10:30" aria-label={t('siteDocs.meeting.to')} {...ende.props} />
      </span>
    </FeldZeile>
  )
}

function NaechsteFelder({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const datum = useFeld(stelle, 'naechsteDatum', { art: 'datum', label: t('siteDocs.meeting.nextDate') })
  const beginn = useFeld(stelle, 'naechsteBeginn', { label: t('siteDocs.meeting.from'), pruefe: ZEIT })
  const ort = useFeld(stelle, 'naechsteOrt', { label: t('siteDocs.meeting.place') })
  return (
    <FeldZeile label={t('siteDocs.meeting.next')} status={datum}>
      <span className="flex flex-wrap items-center gap-1">
        <input type="date" className={`${feldKlasse} w-40 ${datumOhneVorlage(datum.props.value)}`} aria-label={t('siteDocs.meeting.nextDate')} {...datum.props} />
        <input className={`${feldKlasse} w-24`} placeholder="09:00" aria-label={t('siteDocs.meeting.from')} {...beginn.props} />
        <input className={`${feldKlasse} min-w-0 flex-1`} placeholder={t('siteDocs.meeting.place')} aria-label={t('siteDocs.meeting.nextPlace')} {...ort.props} />
      </span>
    </FeldZeile>
  )
}

function EinspruchFelder({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const tage = useFeld<number | null>(stelle, 'einspruchTage', { art: 'zahl', stellen: 0, label: t('siteDocs.meeting.objectionDays') })
  const text = useFeld(stelle, 'einspruchText', { label: t('siteDocs.meeting.objectionText') })
  return (
    <FeldZeile label={t('siteDocs.meeting.objectionPeriod')} status={text}>
      <span className="flex flex-wrap items-start gap-1">
        <input className={`${feldKlasse} w-20 text-right`} inputMode="numeric" aria-label={t('siteDocs.meeting.objectionDays')} {...tage.props} />
        <textarea className={`${feldKlasse} min-h-12 min-w-0 flex-1`} aria-label={t('siteDocs.meeting.objectionText')} {...text.props} />
      </span>
    </FeldZeile>
  )
}

/** Leiste „Fortschreibung" (4.3, 11.1): woher übernommen wurde und die Hinweise — Berichtigung, doppelte Laufnummer. */
function Fortschreibungsleiste({ fort }: { fort: BesprechungFortschreibungAntwort }) {
  const { t } = useTranslation()
  if (!fort.quelle && !fort.hinweise.berichtigung) return null
  return (
    <div className="space-y-1 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-950 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100">
      {fort.quelle && (
        <p>
          {t('siteDocs.meeting.carriedFrom', { nr: fort.quelle.laufnummer })}{' '}
          <Link to={`/site-docs/${fort.quelle.documentId}`} className="text-primary hover:underline">
            {fort.quelle.number}
          </Link>
          {fort.hinweise.erledigt.length > 0 && ` · ${t('siteDocs.meeting.doneHints', { count: fort.hinweise.erledigt.length })}`}
        </p>
      )}
      {fort.hinweise.berichtigung && (
        <p className="font-medium">
          {t('siteDocs.meeting.correctionHint', { nr: fort.quelle?.laufnummer ?? '' })}{' '}
          <Link to={`/site-docs/${fort.hinweise.berichtigung.documentId}`} className="text-primary hover:underline">
            {fort.hinweise.berichtigung.number}
          </Link>
        </p>
      )}
    </div>
  )
}

/**
 * Nach der Freigabe (7.): Versandstand je Teilnehmer und „An den Verteiler senden". „Berichtigung anlegen" ist
 * der Nachtrag des Rahmens (Knopf oben, `POST …/supplement`).
 */
function NachAbschluss({ dok, i, onGeaendert }: { dok: SiteDocDetail; i: BesprechungInhalt; onGeaendert: (neu?: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [dialog, setDialog] = useState(false)
  const verteiler = i.teilnehmer.filter((p) => p.distribution)
  const gesendet = verteiler.filter((p) => p.sentAt)
  const knopf = 'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted disabled:opacity-50'
  const freigegeben = dok.status === 'FINAL'
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {freigegeben && dok.can.send && (
          <button type="button" className={`${knopf} border-primary bg-primary text-primary-foreground hover:bg-primary/90`} disabled={!dok.pdfHash} title={dok.pdfHash ? undefined : t('siteDocs.meeting.pdfPending')} onClick={() => setDialog(true)}>
            <Mail className="h-4 w-4" /> {t('siteDocs.meeting.distribute')}
          </button>
        )}
      </div>
      {verteiler.length > 0 && (
        <div className="space-y-1 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {gesendet.length ? t('siteDocs.meeting.sentCount', { gesendet: gesendet.length, gesamt: verteiler.length }) : t('siteDocs.meeting.notSent')}
          </p>
          <ul className="divide-y rounded-md border">
            {verteiler.map((p) => (
              <li key={p.id ?? p.clientId} className="flex flex-wrap gap-x-3 px-3 py-1">
                <span className="min-w-0 flex-1">{[p.name, p.company].filter(Boolean).join(', ')}</span>
                <span className="text-xs text-muted-foreground">{p.email ?? t('siteDocs.meeting.noAddress')}</span>
                <span className="text-xs">{p.sentAt ? t('siteDocs.meeting.sentOn', { am: new Date(p.sentAt).toLocaleString() }) : t('siteDocs.meeting.notSentShort')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dialog && <VerteilerDialog dok={dok} onClose={() => setDialog(false)} onGesendet={() => onGeaendert()} />}
    </div>
  )
}

/**
 * AP-49 11.1 — **Detail der Projektbesprechung im ERP** (Hauptfläche). Im Entwurf: Leiste „Fortschreibung",
 * Kopf feldweise (AP-36), Teilnehmer, Tagesordnung und Punkte als Zeilenblock; bei der Berichtigung die
 * Einwände und der Verteiler. Nach der Freigabe: Versand an den Verteiler und „Berichtigung anlegen". Liegt
 * der Entwurf auf einem Gerät, steht er hier nur zum Lesen.
 */
export function BesprechungErpDetail({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const i = inhaltVon(dok)
  const entwurf = dok.status === 'DRAFT' && !dok.heldByUserId && dok.can.edit
  const [konfig, setKonfig] = useState<BesprechungKonfig | null>(null)
  const [fort, setFort] = useState<BesprechungFortschreibungAntwort | null>(null)
  useEffect(() => {
    if (!entwurf) return
    besprechungKonfig().then(setKonfig).catch(() => setKonfig(null))
  }, [entwurf])
  useEffect(() => {
    if (!entwurf || i.form !== 'PROTOKOLL') return
    besprechungFortschreibung({ documentId: dok.id, art: i.art }).then(setFort).catch(() => setFort(null))
  }, [entwurf, dok.id, i.form, i.art, i.laufnummer])
  const vorschlaege = useTeilnehmerVorschlaege(dok, entwurf, konfig?.firma ?? '')
  // Zuständig: zuerst die Teilnehmer dieses Protokolls, dann Kontakte und Mitarbeiter.
  const zustaendige = useMemo(
    () => [
      ...i.teilnehmer.map((p) => ({ text: [p.name, p.company].filter(Boolean).join(' — '), name: p.name, company: p.company, function: p.function, email: p.email ?? null, userId: p.userId ?? null, contactId: p.contactId ?? null })),
      ...vorschlaege.filter((v) => !i.teilnehmer.some((p) => p.name === v.name)),
    ],
    [i.teilnehmer, vorschlaege],
  )
  const erledigt = useMemo(() => new Map((fort?.hinweise.erledigt ?? []).map((e) => [e.originId, e.doneAt])), [fort])

  if (dok.contentHash) return <NachAbschluss dok={dok} i={i} onGeaendert={onGeaendert} />
  if (!entwurf) return <BesprechungErpVorzeige fachteil={besprechungFachteil(i)} />

  return (
    <div className="space-y-5">
      {fort && <Fortschreibungsleiste fort={fort} />}
      {/* AP-49 13.: Mitschrift in Punkte — nur mit gebuchtem und eingeschaltetem `SITE_DOCS.AI`. */}
      {dok.can.dictate && i.form === 'PROTOKOLL' && <BesprechungDiktat dok={dok} inhalt={i} onGeaendert={onGeaendert} />}
      <KopfBesprechung dok={dok} i={i} arten={konfig?.arten ?? []} vergeben={fort?.vergeben ?? []} onGeaendert={onGeaendert} />
      {i.form === 'BERICHTIGUNG' ? (
        <>
          <EinwaendeBlock dok={dok} einwaende={i.berichtigungen} onGeaendert={onGeaendert} />
          <TeilnehmerBlock dok={dok} teilnehmer={i.teilnehmer} mitAnwesenheit={false} firma={konfig?.firma ?? ''} onGeaendert={onGeaendert} />
        </>
      ) : (
        <>
          <TeilnehmerBlock dok={dok} teilnehmer={i.teilnehmer} mitAnwesenheit firma={konfig?.firma ?? ''} onGeaendert={onGeaendert} />
          <TagesordnungBlock dok={dok} tops={i.tagesordnung} onGeaendert={onGeaendert} />
          <PunkteBlock dok={dok} tops={i.tagesordnung} punkte={i.punkte} vorschlaege={zustaendige} erledigt={erledigt} onGeaendert={onGeaendert} />
        </>
      )}
      <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.keyboardHint')}</p>
    </div>
  )
}
